import type { LocaleDefinition, LocaleId } from '../domain/types.js';

export const FALLBACK_LOCALE: LocaleId = 'en_US';
export const LOCALE_STORAGE_KEY = 'ertiq.locale';
export const FALLBACK_IMAGE_LANGUAGE = 'en';

export const localeRegistry: Record<LocaleId, LocaleDefinition> = {
  en_US: {
    id: 'en_US',
    label: 'English (US)',
    htmlTag: 'en-US',
    cldrLocale: 'en',
    dir: 'ltr',
    ogLocale: 'en_US',
    fallback: FALLBACK_LOCALE
  },
  en_GB: {
    id: 'en_GB',
    label: 'English (UK)',
    htmlTag: 'en-GB',
    cldrLocale: 'en-GB',
    dir: 'ltr',
    ogLocale: 'en_GB',
    fallback: FALLBACK_LOCALE
  },
  de_DE: {
    id: 'de_DE',
    label: 'Deutsch (DE)',
    htmlTag: 'de-DE',
    cldrLocale: 'de',
    dir: 'ltr',
    ogLocale: 'de_DE',
    fallback: FALLBACK_LOCALE
  },
  hu_HU: {
    id: 'hu_HU',
    label: 'Magyar (HU)',
    htmlTag: 'hu-HU',
    cldrLocale: 'hu',
    dir: 'ltr',
    ogLocale: 'hu_HU',
    fallback: FALLBACK_LOCALE
  },
  ar_SA: {
    id: 'ar_SA',
    label: 'العربية (SA)',
    htmlTag: 'ar-SA',
    cldrLocale: 'ar',
    dir: 'rtl',
    ogLocale: 'ar_SA',
    fallback: FALLBACK_LOCALE
  }
};

export const supportedLocales = Object.keys(localeRegistry) as LocaleId[];

export const canonicalizeLocaleInput = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const normalized = trimmed.replaceAll('-', '_');
  const [language = '', region = ''] = normalized.split('_');

  if (!language) {
    return '';
  }

  return region ? `${language.toLowerCase()}_${region.toUpperCase()}` : language.toLowerCase();
};

export const toHtmlLocaleTag = (value: string): string => canonicalizeLocaleInput(value).replace('_', '-');

export const resolveSupportedLocale = (value: string): LocaleId | null => {
  const normalized = canonicalizeLocaleInput(value);
  if (!normalized) {
    return null;
  }

  if (normalized in localeRegistry) {
    return normalized as LocaleId;
  }

  const [language] = normalized.split('_');
  const languageOnlyMatch = supportedLocales.find((locale) => locale.startsWith(`${language}_`));
  return languageOnlyMatch ?? null;
};

export interface LocaleDetectionInput {
  readonly storedLocale?: string | null;
  readonly queryLocale?: string | null;
  readonly navigatorLocales?: readonly string[];
  readonly navigatorLocale?: string | null;
}

export const detectLocale = (input: LocaleDetectionInput): LocaleId => {
  const candidates = [
    input.storedLocale,
    input.queryLocale,
    ...(input.navigatorLocales ?? []),
    input.navigatorLocale
  ];

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    const resolved = resolveSupportedLocale(candidate);
    if (resolved) {
      return resolved;
    }
  }

  return FALLBACK_LOCALE;
};

export const getLocaleFromUrl = (url: URL): string | null => url.searchParams.get('lang');

export const withLocaleQuery = (url: URL, locale: LocaleId): string => {
  const next = new URL(url.toString());
  next.searchParams.set('lang', locale);
  return `${next.pathname}${next.search}${next.hash}`;
};

export const resolveLocalizedImageLanguage = (locale: LocaleId): string => {
  if (locale === 'hu_HU') {
    return 'hu';
  }

  return FALLBACK_IMAGE_LANGUAGE;
};

export const resolveLocalizedImagePath = (locale: LocaleId, fileName: string): string =>
  `/resources/img/${resolveLocalizedImageLanguage(locale)}/${fileName}`;
